import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SupportedLocale } from "@/i18n";
import { mapFetchedMultiplePrint } from "@/api/multipleprint/multipleprint";
import {
  computeFieldMultipleprintSummaryDimensions,
  computeFieldMultipleprintSupportTechnique,
} from "@/lib/computeFields";
import { tWithKey } from "@/lib/i18n-fallback";
import { getDefaultAttachment } from "@/lib/image";
import {
  FetchedMultipleprint,
  Multipleprint,
  MultipleprintPortfolioItem,
} from "@/lib/types";
import { badgeVariants } from "@/components/ui/badge";
import { cn, formatTextWithColon } from "@/lib/utils";
import { SupportTechniqueCF } from "@/lib/compute-field/shared/technique";
import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { MultipleprintFullscreenViewer } from "./MultipleprintFullscreenViewer";

// KEEP IN SYNC : inventozen-frontpublic/src/components/pages/catalogueRaisonne/multiplePage/MultipleprintPortfolioBlock.tsx

interface MultipleprintPortfolioBlockProps {
  data: Multipleprint;
  locale: SupportedLocale;
  t: (key: string) => string;
  /** Canvas du CMS avancé : pas de navigation (on quitterait l'éditeur). */
  embedded?: boolean;
  /** Fiche exemplaire : seulement le rappel du portfolio du multiple parent (pas la modale). */
  partOfOnly?: boolean;
}

type WithStatus = { estampesMultiplesPublicationStatus?: string };

// Même style que « Lire plus » (ReadMoreHtml).
const readMoreLinkClass =
  "text-muted-foreground font-semibold no-underline hover:opacity-80";

const isPublished = (record: WithStatus) =>
  record.estampesMultiplesPublicationStatus === "published";

// La prévisu montre aussi les brouillons (étiquetés) ; la corbeille reste masquée.
const isVisibleInPreview = (record: WithStatus) =>
  record.estampesMultiplesPublicationStatus !== "bin";

/**
 * Avant les notes publiques :
 * - simple → « Fait partie du portfolio : » + lien(s) ;
 * - portfolio → bouton qui ouvre la liste de ses multiples simples.
 * Sur un exemplaire, `data` est son multiple parent : seul le rappel s'applique.
 * Les brouillons sont affichés avec une étiquette ; leur « Voir la fiche » est grisé.
 */
export function MultipleprintPortfolioBlock({
  data,
  locale,
  t,
  embedded = false,
  partOfOnly = false,
}: MultipleprintPortfolioBlockProps) {
  // Contrôlée : la fiche cible réutilise ce composant, la modale doit se fermer au clic.
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<MultipleprintPortfolioItem | null>(null);
  // Pas de fiche à ouvrir pour un brouillon, ni depuis le canvas du CMS.
  const itemHref = (item: MultipleprintPortfolioItem) =>
    embedded || !isPublished(item)
      ? undefined
      : `/edit/multipleprint/overview/${item.id}`;
  const closeAll = () => {
    setSelectedItem(null);
    setOpen(false);
  };
  const overviewLink = (
    record: WithStatus & { id: string },
    children: ReactNode,
  ) => (
    <>
      {embedded ? (
        // Canvas du CMS : même rendu, sans navigation.
        <span className={readMoreLinkClass}>{children}</span>
      ) : (
        <Link
          to={`/edit/multipleprint/overview/${record.id}`}
          className={readMoreLinkClass}
        >
          {children}
        </Link>
      )}
      {record.estampesMultiplesPublicationStatus !== "published" && (
        <span
          className={cn(
            badgeVariants({ variant: "draft" }),
            "ml-2 align-middle",
          )}
        >
          {t("draft")}
        </span>
      )}
    </>
  );

  if (data.estampesMultiplesNature === "portfolio") {
    if (partOfOnly) return null;
    const items = (data.portfolioItems ?? []).filter(isVisibleInPreview);
    if (items.length === 0) return null;

    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            type="button"
            // Même style que « Lire plus » (ReadMoreHtml).
            className="mt-4 self-start text-left text-muted-foreground text-sm font-semibold no-underline hover:opacity-80"
          >
            {tWithKey(
              t,
              "multipleprint.portfolio.showItems",
              "Voir les multiples du portfolio",
            )}{" "}
            ({items.length})
          </button>
        </DialogTrigger>
        <DialogContent
          // Masquée sous le plein écran d'un multiple (rendu dans son propre portail).
          className={cn(
            "w-[95vw] max-w-6xl max-h-[85vh] overflow-y-auto",
            selectedItem && "invisible",
          )}
        >
          <DialogHeader>
            <DialogTitle>{data.estampesMultiplesDescriptionTitre}</DialogTitle>
            <DialogDescription>
              {tWithKey(
                t,
                "multipleprint.portfolio.items",
                "Multiples du portfolio",
              )}{" "}
              ({items.length})
            </DialogDescription>
          </DialogHeader>
          {/* Même grille que la recherche d'œuvres du front public (ArtCard). */}
          <ul className="m-0 grid list-none grid-cols-1 gap-x-6 gap-y-8 p-0 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((item) => (
              <li key={item.id}>
                <PortfolioItemCard
                  item={item}
                  locale={locale}
                  t={t}
                  href={itemHref(item)}
                  onOpen={() => setSelectedItem(item)}
                  onNavigate={closeAll}
                />
              </li>
            ))}
          </ul>
          {selectedItem && (
            <PortfolioItemFullscreen
              item={selectedItem}
              locale={locale}
              t={t}
              href={itemHref(selectedItem)}
              onClose={() => setSelectedItem(null)}
              onNavigate={closeAll}
            />
          )}
        </DialogContent>
      </Dialog>
    );
  }

  const portfolios = (data.inPortfolios ?? []).filter(isVisibleInPreview);
  if (portfolios.length === 0) return null;

  return (
    <p className="mt-4 font-normal text-sm">
      {formatTextWithColon(
        tWithKey(
          t,
          "multipleprint.portfolio.partOf",
          "Fait partie du portfolio",
        ),
        locale,
        "",
      )}
      {portfolios.map((portfolio, index) => (
        <span key={portfolio.id}>
          {index > 0 && ", "}
          {overviewLink(
            portfolio,
            portfolio.estampesMultiplesDescriptionTitre ||
              portfolio.estampesMultiplesDescriptionNumeroEnregistrement,
          )}
        </span>
      ))}
    </p>
  );
}

/** « Voir la fiche » : blanc à filet noir ; grisé quand il n'y a pas de fiche à ouvrir. */
function ViewSheetButton({
  href,
  t,
  onNavigate,
  className,
}: {
  href?: string;
  t: (key: string) => string;
  onNavigate: () => void;
  className?: string;
}) {
  const label = tWithKey(
    t,
    "multipleprint.portfolio.viewSheet",
    "Voir la fiche",
  );
  const base =
    "inline-flex items-center justify-center rounded-md border px-4 py-2 text-center text-sm font-medium";

  return href ? (
    <Link
      to={href}
      onClick={onNavigate}
      className={cn(
        base,
        // Survol : bleu clair (teinte du bleu InventoZen).
        "border-black bg-white text-black hover:border-[#008bd4] hover:bg-[#e6f4fb] hover:text-[#008bd4]",
        className,
      )}
    >
      {label}
    </Link>
  ) : (
    <button
      type="button"
      disabled
      className={cn(
        base,
        "cursor-not-allowed border-slate-300 bg-white/70 text-slate-400",
        className,
      )}
    >
      {label}
    </button>
  );
}

/**
 * Carte d'un multiple du portfolio, calquée sur ArtCard (recherche d'œuvres du front) :
 * vignette dans une case carrée, légende centrée courte (clic → plein écran),
 * puis « Voir la fiche ».
 */
function PortfolioItemCard({
  item,
  locale,
  t,
  href,
  onOpen,
  onNavigate,
}: {
  item: MultipleprintPortfolioItem;
  locale: SupportedLocale;
  t: (key: string) => string;
  href?: string;
  onOpen: () => void;
  onNavigate: () => void;
}) {
  const attachment = getDefaultAttachment(
    item.attachments ?? [],
    "multipleprint",
  );
  const imageUrl =
    attachment?.mediumPublicUrl ??
    attachment?.largePublicUrl ??
    attachment?.posterPublicUrl ??
    undefined;
  const registrationNumber =
    item.estampesMultiplesDescriptionNumeroEnregistrement;
  const title = item.estampesMultiplesDescriptionTitre || registrationNumber;
  // N° d'enregistrement affiché seul (sans libellé) ; le libellé passe en bulle d'aide.
  // On l'affiche uniquement en complément d'un vrai titre (sinon il est déjà le titre).
  const showRegistrationNumber = Boolean(
    registrationNumber && item.estampesMultiplesDescriptionTitre,
  );
  const registrationNumberLabel = tWithKey(
    t,
    "multipleprint.portfolio.registrationNumber",
    "N° d'enregistrement",
  );

  return (
    <div className="flex h-fit flex-col items-center">
      <button
        type="button"
        onClick={onOpen}
        className="group block w-full cursor-pointer text-left"
      >
        {/* Case carrée ; l'image garde ses proportions. */}
        <div className="flex aspect-square w-full items-center justify-center [container-type:inline-size]">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={title}
              className="block h-auto w-auto max-w-full max-h-[100cqw] transition-transform group-hover:scale-[1.02]"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-slate-50">
              <span className="text-xs text-slate-400">{t("noImage")}</span>
            </div>
          )}
        </div>
        {/* Texte toujours noir : pas d'effet de lien au survol. */}
        <div className="pt-2 text-center text-sm leading-snug">
          {showRegistrationNumber && (
            <p
              className="cursor-help text-xs text-muted-foreground"
              title={registrationNumberLabel}
            >
              {registrationNumber}
            </p>
          )}
          <p className="font-semibold">{title}</p>
          {item.estampesMultiplesDescriptionDateDeCreation && (
            <p>{item.estampesMultiplesDescriptionDateDeCreation}</p>
          )}
          <p className="text-xs">
            <SupportTechniqueCF
              input={{
                technique: item.estampesMultiplesDescriptionTechnique,
                support: item.estampesMultiplesDescriptionSupportMateriau,
                preposition: t("computeField.techniqueSupportPreposition"),
              }}
              language={locale}
              lineBreak={false}
            />
          </p>
        </div>
      </button>
      <ViewSheetButton
        href={href}
        t={t}
        onNavigate={onNavigate}
        className="mt-3"
      />
    </div>
  );
}

/**
 * Plein écran d'un multiple du portfolio (même visualiseur que les images de la fiche),
 * avec « Voir la fiche ».
 */
function PortfolioItemFullscreen({
  item,
  locale,
  t,
  href,
  onClose,
  onNavigate,
}: {
  item: MultipleprintPortfolioItem;
  locale: SupportedLocale;
  t: (key: string) => string;
  href?: string;
  onClose: () => void;
  onNavigate: () => void;
}) {
  // Les multiples imbriqués arrivent bruts de l'API (dimensions en JSON texte).
  const data = mapFetchedMultiplePrint(item as unknown as FetchedMultipleprint);
  const attachment = getDefaultAttachment(
    item.attachments ?? [],
    "multipleprint",
  );
  if (!attachment) return null;

  return (
    <MultipleprintFullscreenViewer
      open
      stacked
      onClose={onClose}
      attachment={attachment}
      data={data}
      locale={locale}
      t={t}
      dimensionsSummaryCalculated={computeFieldMultipleprintSummaryDimensions(
        data,
        { language: locale, translation: t },
      )}
      supportTechniqueCalculated={computeFieldMultipleprintSupportTechnique(
        data,
        { language: locale, translation: t },
      )}
      footer={
        <ViewSheetButton
          href={href}
          t={t}
          onNavigate={onNavigate}
          className="mt-6"
        />
      }
    />
  );
}
